import React from "react";
import ReactDOM from "react-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import moment from "moment";
import "./ScoreboardPDFReport.css";
import vozoLogo from "../../../assets/images/vozo-logo-1.png";

/**
 * PdfGaugeCard - reusable gauge + scoring guide for a single score.
 * Mirrors SingleGaugeDisplay in Scoreboard.js.
 */
const PdfGaugeCard = ({ title, score, guideArray }) => {
    const getDefaultColors = (count) => {
        if (count === 3) return ["#27AE60", "#F2C94C", "#EB5757"];
        if (count === 4) return ["#56CCF2", "#27AE60", "#F2994A", "#EB5757"];
        if (count === 5) return ["#56CCF2", "#27AE60", "#F2C94C", "#F2994A", "#EB5757"];
        return ["#56CCF2", "#27AE60", "#F2C94C", "#F2994A", "#EB5757"];
    };

    const colors = getDefaultColors(guideArray.length);
    const dynamicGuide = guideArray.map((item, index) => ({
        min: item.min,
        max: item.max,
        severity: item.label || item.severity,
        score: item.min === item.max ? String(item.min) : `${item.min}-${item.max}`,
        color: colors[index] || "#999",
    }));

    const maxScore = dynamicGuide.length ? Math.max(...dynamicGuide.map(g => g.max)) : 21;

    // Find active segment — handle out-of-range gracefully
    let activeIndex = dynamicGuide.findIndex(g => score >= g.min && score <= g.max);
    if (activeIndex === -1 && dynamicGuide.length) {
        activeIndex = score > dynamicGuide[dynamicGuide.length - 1].max
            ? dynamicGuide.length - 1
            : 0;
    }

    const severityLevel = dynamicGuide[activeIndex] || dynamicGuide[0];

    const radius = 80;
    const center = 100;
    const GAP = 14;
    const TOTAL_ARC = 180;
    const SEGMENT_COUNT = dynamicGuide.length || 1;
    const segmentAngle = SEGMENT_COUNT > 1
        ? (TOTAL_ARC - GAP * (SEGMENT_COUNT - 1)) / SEGMENT_COUNT
        : TOTAL_ARC;

    const polarToCartesian = (cx, cy, r, angle) => {
        const rad = (angle - 180) * (Math.PI / 180);
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };

    const describeArc = (startAngle, endAngle, color) => {
        const strokeWidth = 12;
        const start = polarToCartesian(center, center, radius, endAngle);
        const end = polarToCartesian(center, center, radius, startAngle);
        return (
            <path
                d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 0 0 ${end.x} ${end.y}`}
                stroke={color}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
            />
        );
    };

    return (
        <div className="pdf-score-summary-card">
            <div className="pdf-gauge-inner-card">
                <div className="pdf-gauge-label-top">{title || "Score"}</div>
                <div className="pdf-gauge-svg-wrapper">
                    <svg width="250" height="140" viewBox="0 0 200 120">
                        {dynamicGuide.map((item, i) => {
                            const start = i * (segmentAngle + GAP);
                            const end = start + segmentAngle;
                            const strokeColor = i <= activeIndex ? item.color : "#E0E0E0";
                            return <React.Fragment key={i}>{describeArc(start, end, strokeColor)}</React.Fragment>;
                        })}
                        {Array.from({ length: maxScore + 1 }).map((_, i) => {
                            const angle = 5 + ((175 - 5) / maxScore) * i;
                            const tickRadius = radius - 15;
                            const { x, y } = polarToCartesian(center, center, tickRadius, angle);
                            return <circle key={i} cx={x} cy={y} r="1" fill="#BDBDBD" />;
                        })}
                        <text x="10" y="115" className="pdf-gauge-tick-text">0</text>
                        <text x="180" y="115" className="pdf-gauge-tick-text">{maxScore}</text>
                    </svg>
                </div>
                <div className="pdf-gauge-value-overlay">
                    <div className="pdf-value-big">{score}</div>
                    <div className="pdf-value-label">{severityLevel?.severity}</div>
                </div>
            </div>

            <div className="pdf-guide-column">
                <div className="pdf-guide-header-title">{title ? `${title} Scoring Guide` : "Scoring Guide"}</div>
                <table className="pdf-guide-data-table">
                    <thead>
                        <tr>
                            <th style={{ color: "#2F80ED" }}>Score</th>
                            <th className="scoring-guide-th">Severity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dynamicGuide.map((item, idx) => (
                            <tr key={idx}>
                                <td>
                                    <span className="pdf-color-dot" style={{ backgroundColor: item.color }} />
                                    {item.score}
                                </td>
                                <td>{item.severity}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/**
 * ScoreboardReportComponent - The visual template for the PDF
 */
export const ScoreboardReportComponent = React.forwardRef(({ rowData, patientInfo, facilityInfo, assessmentName }, ref) => {
    const currentDate = moment().format("DD/MM/YYYY");

    // Total score across all Assessment_values (used for single-score fallback)
    const currentScore = rowData?.Assessment_values?.reduce((sum, item) => sum + (item?.score || 0), 0) || 0;

    const scoringData = (rowData?.Assessment_question || []).map((q, index) => {
        const selected = rowData?.Assessment_values?.find(v => v.question_id === q.question_id);

        let optionsForQuestion = [];
        if (Array.isArray(rowData?.Assessment_options)) {
            optionsForQuestion = rowData.Assessment_options.filter(
                (o) => o.question_id === q.question_id,
            );
        } else if (rowData?.Assessment_options && typeof rowData.Assessment_options === 'object') {
            optionsForQuestion = rowData.Assessment_options[q.question_id] || [];
        }

        const maxScoreForQuestion = optionsForQuestion.length
            ? Math.max(...optionsForQuestion.map((o) => o.score || 0))
            : 0;

        let responseText = "";
        if (selected) {
            if (Array.isArray(selected.selected_option_id)) {
                const matchedOptions = optionsForQuestion.filter(o =>
                    selected.selected_option_id.includes(o.option_id)
                );
                if (matchedOptions.length > 0) {
                    responseText = matchedOptions.map(o => o.option).join(", ");
                } else {
                    responseText = selected.selected_option_id.join(", ");
                }
            } else {
                const selectedOption = optionsForQuestion.find(
                    (o) => o.option_id === selected.selected_option_id
                );
                responseText = selectedOption?.option || selected.value || selected.selected_option || selected.selected_option_id;
            }
        }

        let additionalAnswersUI = null;
        if (q.additional_questions && typeof q.additional_questions === 'object') {
            let addVals = null;
            try {
                if (typeof rowData.additional_values === 'string') addVals = JSON.parse(rowData.additional_values);
                else addVals = rowData.additional_values;

                if (!addVals) {
                    if (typeof rowData.Patient_additional_values === 'string') addVals = JSON.parse(rowData.Patient_additional_values);
                    else addVals = rowData.Patient_additional_values;
                }
            } catch (e) { }

            if (addVals && responseText && q.additional_questions[responseText]) {
                const addQs = q.additional_questions[responseText];
                if (Array.isArray(addQs)) {
                    additionalAnswersUI = addQs.map((aq) => {
                        let ansObj = null;
                        if (Array.isArray(addVals)) {
                            ansObj = addVals.find(a => a.question_id === aq.question_id);
                        } else {
                            ansObj = addVals[aq.question_id];
                        }

                        if (ansObj && ansObj.value) {
                            return (
                                <div key={aq.question_id} style={{ marginTop: '4px', fontSize: '11px', color: '#555', borderTop: '1px dashed #e8e8e8', paddingTop: '4px' }}>
                                    <span style={{ fontWeight: 500, marginRight: '4px' }}>{aq.question}</span> {ansObj.value}
                                </div>
                            );
                        }
                        return null;
                    });
                }
            }
        }

        const isText = q.type === 'text';

        // Mark whether this question was actually attempted by the patient
        const isAttempted =
            !!selected ||
            (isText && responseText && responseText !== "-" && responseText !== "");

        const responseNode = (
            <div>
                <div style={{ fontWeight: additionalAnswersUI ? 500 : 400 }}>{responseText || "-"}</div>
                {additionalAnswersUI}
            </div>
        );

        const currentAssessment =
            rowData?.screen_form_name ||
            rowData?.form_name ||
            rowData?.assessment_name ||
            rowData?.assess_form ||
            rowData?.Assessment_name;

        const nonScoredQIds = [
            "QA2",
            "Q27", "Q28", "Q29", "Q30", "Q31", "Q32",
            "27", "28", "29", "30", "31", "32",
            "QA27", "QA28", "QA29", "QA30", "QA31", "QA32"
        ];

        const hideScore =
            (currentAssessment === "EAT-26" || currentAssessment?.trim() === "EAT-26") &&
            nonScoredQIds.includes(String(q.question_id));

        return {
            sno: q.question_no || (index + 1),
            question: q.question,
            response: responseNode,
            score: (isText || hideScore) ? "" : (selected ? `${selected.score || 0}/${maxScoreForQuestion}` : `- / ${maxScoreForQuestion}`),
            isAttempted, // internal flag — used for filtering below
            section: q.section || "",
        };
    })
        // Show only questions the patient has actually answered
        .filter((row) => row.isAttempted);

    // Group questions by section for rendering
    const sectionGroups = [];
    let currentGroup = null;
    let lastKnownSection = "";
    scoringData.forEach((row) => {
        const sec = row.section || lastKnownSection;
        if (row.section) lastKnownSection = row.section;
        if (!currentGroup || (row.section && currentGroup.section !== sec)) {
            currentGroup = { section: sec, rows: [] };
            sectionGroups.push(currentGroup);
        }
        currentGroup.rows.push(row);
    });
    const hasAnySections = sectionGroups.length > 1 || (sectionGroups.length === 1 && sectionGroups[0].section);



    return (
        <div ref={ref} className="scoreboard-pdf-export-container" id="scoreboard-report-template">
            <div className="pdf-report-header">
                <div className="pdf-report-logo">
                    <img src={vozoLogo} alt="VOZO" className="pdf-logo-img" />
                </div>
                <div className="pdf-report-date">Date : {currentDate}</div>
            </div>

            <div className="pdf-patient-provider-details">
                <div className="pdf-detail-section">
                    <div className="pdf-detail-item">
                        <span className="pdf-label-bold">Patient Name:</span>
                        <span className="pdf-value-text">{patientInfo?.fname} {patientInfo?.mname ? patientInfo?.mname + ' ' : ''}{patientInfo?.lname}</span>
                    </div>
                    <div className="pdf-detail-item">
                        <span className="pdf-label-bold">Date of Birth:</span>
                        <span className="pdf-value-text">{patientInfo?.dob ? moment(patientInfo.dob).format("DD/MM/YYYY") : "-"}</span>
                    </div>
                </div>
                <div className="pdf-detail-section pdf-right-block">
                    <div className="pdf-detail-item">
                        <span className="pdf-label-bold">Provider Name:</span>
                        <span className="pdf-value-text">{patientInfo?.provider_name || facilityInfo?.provider_name || "-"}</span>
                    </div>
                    <div className="pdf-facility-info">
                        <div>{facilityInfo?.name || "-"}</div>
                        <div>{facilityInfo?.street || "-"}, {facilityInfo?.city || "-"}, {facilityInfo?.state || "-"} {facilityInfo?.zip || ""}</div>
                        <div>PH: {facilityInfo?.phone || "-"}</div>
                    </div>
                </div>
            </div>

            <div className="pdf-horizontal-divider"></div>

            <h2 className="pdf-assessment-title">
                {assessmentName
                    ? assessmentName
                    : rowData?.type === "normal_without_score" || rowData?.type === "skip_without_score"
                        ? (rowData?.form_name || "Assessment")
                        : (rowData?.form_name?.includes("Scoring Guide") ? rowData.form_name : `${rowData?.form_name || "Assessment"} Scoring Guide`)}
            </h2>

            {/* Rule-based scoreboard (e.g., PHQ) — rendered HTML from backend */}
            {rowData?.scoreboard && (
                <div
                    className="scoreboard-html-table"
                    dangerouslySetInnerHTML={{ __html: rowData.scoreboard }}
                />
            )}

            {/* ── Multi-score / Single-score gauges — mirrors Scoreboard.js logic exactly ── */}
            {rowData?.type !== "normal_without_score" && rowData?.type !== "skip_without_score" && !rowData?.scoreboard && (
                rowData?.scores && Array.isArray(rowData.scores) && rowData.scores.length > 0 ? (
                    // MULTI-SCORE: one PdfGaugeCard per score entry
                    rowData.scores.map((s, idx) => {
                        let guide = [];
                        if (rowData.scoring_guide && !Array.isArray(rowData.scoring_guide)) {
                            // keyed object — look up by score id
                            guide = rowData.scoring_guide[s.id] || rowData.scoring_guide[s.id?.toLowerCase()] || [];
                        } else if (Array.isArray(rowData.scoring_guide)) {
                            guide = rowData.scoring_guide;
                        }
                        const displayTitle = s.id
                            ? s.id.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                            : (rowData.screen_form_name || "Score");
                        return (
                            <PdfGaugeCard
                                key={idx}
                                title={displayTitle}
                                score={s.score}
                                guideArray={guide}
                            />
                        );
                    })
                ) : (
                    // SINGLE-SCORE: flat scoring_guide array
                    <PdfGaugeCard
                        title={rowData?.screen_form_name || assessmentName || rowData?.form_name || "Score"}
                        score={currentScore}
                        guideArray={Array.isArray(rowData?.scoring_guide) ? rowData.scoring_guide : []}
                    />
                )
            )}

            {/* ── Subscale scoring guide table ── */}
            {rowData?.subscale_scoring_guide && Array.isArray(rowData.subscale_scoring_guide) && (
                <div className="pdf-subscale-section">
                    <table className="pdf-subscale-table">
                        <thead>
                            <tr>
                                <th className="pdf-subscale-header-title">Subscale</th>
                                <th className="pdf-subscale-header-score">Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rowData.subscale_scoring_guide.map((sub, idx) => (
                                <tr key={idx} className="pdf-subscale-row">
                                    <td className="pdf-subscale-title-desc">
                                        <div className="pdf-subscale-title-text">
                                            {sub.subscale || sub.title || sub.name || sub.subscale_name}
                                        </div>
                                        {(sub.description || sub.desc) && (
                                            <div className="pdf-subscale-desc-text">{sub.description || sub.desc}</div>
                                        )}
                                    </td>
                                    <td className="pdf-subscale-score-value">{sub.score}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {rowData?.headers && (
                <div
                    className="pdf-header-content"
                    dangerouslySetInnerHTML={{ __html: rowData.headers }}
                />
            )}

            <div className="pdf-responses-table-wrapper">
                {hasAnySections ? (
                    <>
                        <table className="pdf-responses-table" style={{ borderBottom: "2px solid #cbd5e1" }}>
                            <thead>
                                <tr>
                                    <th style={{ width: "7%", textAlign: "center" }}>S.No</th>
                                    <th style={{ width: rowData?.type === "normal_without_score" || rowData?.type === "skip_without_score" ? "65%" : "53%" }}>Questions</th>
                                    <th style={{ width: "28%" }}>Response</th>
                                    {rowData?.type !== "normal_without_score" && rowData?.type !== "skip_without_score" && (
                                        <th style={{ width: "12%", textAlign: "center" }}>Score</th>
                                    )}
                                </tr>
                            </thead>
                        </table>
                        {sectionGroups.map((group, gIdx) => (
                            <div key={gIdx}>
                                {group.section && (
                                    <div
                                        style={{
                                            background: "#f0f4f8",
                                            padding: "10px 16px",
                                            fontWeight: 600,
                                            fontSize: "15px",
                                            color: "#1e293b",
                                            borderTop: gIdx !== 0 ? "1px solid #e2e8f0" : "none",
                                            borderBottom: "1px solid #e2e8f0",
                                            textAlign: "left"
                                        }}
                                    >
                                        <span dangerouslySetInnerHTML={{ __html: group.section }} />
                                    </div>
                                )}
                                <table className="pdf-responses-table">
                                    <tbody>
                                        {group.rows.map((row, idx) => (
                                            <tr key={idx}>
                                                <td style={{ width: "7%", textAlign: "center", verticalAlign: "middle" }}>{row.sno}</td>
                                                <td style={{ width: rowData?.type === "normal_without_score" || rowData?.type === "skip_without_score" ? "65%" : "53%", verticalAlign: "middle" }}>{row.question}</td>
                                                <td style={{ width: "28%", verticalAlign: "middle" }}>{row.response}</td>
                                                {rowData?.type !== "normal_without_score" && rowData?.type !== "skip_without_score" && (
                                                    <td style={{ width: "12%", textAlign: "center", verticalAlign: "middle" }}>{row.score}</td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </>
                ) : (
                    <table className="pdf-responses-table">
                        <thead>
                            <tr>
                                <th style={{ width: "7%", textAlign: "center" }}>S.No</th>
                                <th style={{ width: rowData?.type === "normal_without_score" || rowData?.type === "skip_without_score" ? "65%" : "53%" }}>Questions</th>
                                <th style={{ width: "28%" }}>Response</th>
                                {rowData?.type !== "normal_without_score" && rowData?.type !== "skip_without_score" && (
                                    <th style={{ width: "12%", textAlign: "center" }}>Score</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {scoringData.map((row, idx) => (
                                <tr key={idx}>
                                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>{row.sno}</td>
                                    <td style={{ verticalAlign: "middle" }}>{row.question}</td>
                                    <td style={{ verticalAlign: "middle" }}>{row.response}</td>
                                    {rowData?.type !== "normal_without_score" && rowData?.type !== "skip_without_score" && (
                                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>{row.score}</td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {rowData?.footers && (
                <div
                    className="pdf-footer-content"
                    dangerouslySetInnerHTML={{ __html: rowData.footers }}
                />
            )}

            <div className="pdf-report-footer">
                Powered by VOZO Documentation
            </div>
        </div>
    );
});

/**
 * downloadScoreboardPDF - The main export function to trigger the PDF download
 * It captures a temporary hidden instance of the component as PNG and puts it in a PDF
 */
/**
 * downloadScoreboardPDF - The main export function to trigger the PDF download
 * This uses the useRef logic implemented similarly to PatientFormPreview.
 */
export const downloadScoreboardPDF = async (pdfRef, rowData, patientInfo, facilityInfo) => {
    if (pdfRef && pdfRef.current) {
        // Wait a moment for rendering to complete (especially images/fonts)
        setTimeout(() => {
            html2canvas(pdfRef.current, { scale: 1.5, useCORS: true })
                .then((canvas) => {
                    // Use JPEG to dramatically reduce file size
                    const imgData = canvas.toDataURL("image/jpeg", 0.75);

                    const pdf = new jsPDF("p", "mm", "a4");
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = pdf.internal.pageSize.getHeight();

                    const margin = 15; // 15mm margin on all sides
                    const contentWidth = pdfWidth - margin * 2;
                    const contentHeight = pdfHeight - margin * 2;

                    const ratio = contentWidth / canvas.width;
                    const imgHeightInMm = canvas.height * ratio;

                    let heightLeft = imgHeightInMm;
                    let position = 0;

                    // Add first page image
                    pdf.addImage(
                        imgData,
                        "JPEG",
                        margin,
                        margin,
                        contentWidth,
                        imgHeightInMm,
                    );
                    heightLeft -= contentHeight;

                    // Mask the top and bottom margins to create a clean cutoff
                    pdf.setFillColor(255, 255, 255);
                    pdf.rect(0, 0, pdfWidth, margin, "F"); // Top margin mask
                    pdf.rect(0, pdfHeight - margin, pdfWidth, margin, "F"); // Bottom margin mask

                    while (heightLeft > 0) {
                        position -= contentHeight;
                        pdf.addPage();

                        // Add image shifted up for the next page
                        pdf.addImage(
                            imgData,
                            "JPEG",
                            margin,
                            margin + position,
                            contentWidth,
                            imgHeightInMm,
                        );
                        heightLeft -= contentHeight;

                        // Mask the top and bottom margins for this page too
                        pdf.setFillColor(255, 255, 255);
                        pdf.rect(0, 0, pdfWidth, margin, "F");
                        pdf.rect(0, pdfHeight - margin, pdfWidth, margin, "F");
                    }

                    const fileName = `${rowData?.form_name || "Scoreboard"}_${moment().format("YYYY-MM-DD")}.pdf`;
                    pdf.save(fileName);
                })
                .catch((error) => {
                    console.error("Error generating Scoreboard PDF:", error);
                });
        }, 150);
    }
};

/*
// Original logic commented out for reference as requested:
export const downloadScoreboardPDF = async (rowData, patientInfo, facilityInfo) => {
    // 1. Create a temporary container for rendering the component
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.top = "-9999px";
    container.style.left = "-9999px";
    container.style.width = "1200px";
    document.body.appendChild(container);

    try {
        // 2. Render the component into the temporary container
        ReactDOM.render(
            <ScoreboardReportComponent
                rowData={rowData}
                patientInfo={patientInfo}
                facilityInfo={facilityInfo}
            />,
            container
        );

        // 3. Wait a moment for rendering to complete (especially images/fonts)
        await new Promise(resolve => setTimeout(resolve, 500));

        // 4. Capture as PNG using html2canvas
        const element = container.querySelector("#scoreboard-report-template");
        const canvas = await html2canvas(element, {
            scale: 1,
            useCORS: true,
            backgroundColor: "#ffffff",
            logging: false
        });

        // 5. Convert to PDF using jsPDF
        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 5;
        const maxLineWidth = pageWidth - (margin * 2);
        const maxLineHeight = pageHeight - (margin * 2);

        let imgWidth = maxLineWidth;
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        // If the image is taller than the page, scale it down to fit vertically
        if (imgHeight > maxLineHeight) {
            const ratio = maxLineHeight / imgHeight;
            imgHeight = maxLineHeight;
            imgWidth = imgWidth * ratio;
        }

        // Center horizontally
        const xPos = (pageWidth - imgWidth) / 2;
        // Center vertically or start from margin
        // const yPos = imgHeight < maxLineHeight ? (pageHeight - imgHeight) / 2 : margin;
        const yPos = margin;
        pdf.addImage(imgData, "JPEG", xPos, yPos, imgWidth, imgHeight);

        // 6. Save the PDF
        const fileName = `${rowData?.form_name || "Scoreboard"}_${moment().format("YYYY-MM-DD")}.pdf`;
        pdf.save(fileName);

    } catch (error) {
        console.error("Error generating Scoreboard PDF:", error);
    } finally {
        // 7. Cleanup
        ReactDOM.unmountComponentAtNode(container);
        document.body.removeChild(container);
    }
};
*/
